import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign,
  Calendar,
  Filter,
  X,
  Search,
  Download,
  ChevronDown,
  ChevronUp,
  Trash2
} from 'lucide-react';
import { BudgetingService } from '../../lib/budgetingService';
import { useAuth } from '../../contexts/AuthContext';

import { Database } from '../../types/supabase';

type BudgetTransaction = Database['public']['Tables']['budget_transactions']['Row'] & {
  budget_categories: {
    id: string;
    name: string;
    type: string;
    color: string;
    icon: string;
  } | null;
};

type BudgetCategory = Database['public']['Tables']['budget_categories']['Row'];

interface FilterState {
  type: 'all' | 'income' | 'expense' | 'transfer';
  category: string;
  dateFrom: string;
  dateTo: string;
  search: string;
}

export function Transactions() {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState<BudgetTransaction[]>([]);
  const [categories, setCategories] = useState<BudgetCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterState>({
    type: 'all',
    category: 'all',
    dateFrom: '',
    dateTo: '',
    search: ''
  });

  useEffect(() => {
    loadData();
    
    // Listen for refresh events
    const onRefresh = () => loadData();
    window.addEventListener('budget:refresh', onRefresh);
    
    return () => {
      window.removeEventListener('budget:refresh', onRefresh);
    };
  }, [user?.id]);

  const loadData = async () => {
    if (!user?.id) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const [trans, cats] = await Promise.all([
        BudgetingService.getBudgetTransactions(user.id),
        BudgetingService.getBudgetCategories(user.id)
      ]);
      
      // Ensure we have valid transaction data from Supabase
      const validTransactions = (trans || []).filter(t => t && t.id) as BudgetTransaction[];
      
      setTransactions(validTransactions);
      setCategories(cats || []);
    } catch (error) {
      console.error('Error loading transactions:', error);
      setTransactions([]);
      setCategories([]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'income':
        return <TrendingUp className="w-5 h-5 text-green-600" />;
      case 'expense':
        return <TrendingDown className="w-5 h-5 text-red-600" />;
      default:
        return <DollarSign className="w-5 h-5 text-gray-600" />;
    }
  };

  const getTransactionColor = (type: string) => {
    switch (type) {
      case 'income':
        return 'text-green-600';
      case 'expense':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getTransactionBgColor = (type: string) => {
    switch (type) {
      case 'income':
        return 'bg-green-100';
      case 'expense':
        return 'bg-red-100';
      default:
        return 'bg-gray-100';
    }
  };

  // Filter transactions
  const filteredTransactions = transactions.filter(transaction => {
    // Type filter
    if (filters.type !== 'all' && transaction.type !== filters.type) {
      return false;
    }

    // Category filter
    if (filters.category !== 'all' && transaction.budget_categories?.id !== filters.category) {
      return false;
    }

    // Date range filter
    if (filters.dateFrom) {
      const transactionDate = new Date(transaction.transaction_date);
      const fromDate = new Date(filters.dateFrom);
      if (transactionDate < fromDate) return false;
    }
    if (filters.dateTo) {
      const transactionDate = new Date(transaction.transaction_date);
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999); // Include the entire end date
      if (transactionDate > toDate) return false;
    }

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const matchesDescription = transaction.description?.toLowerCase().includes(searchLower);
      const matchesCategory = transaction.budget_categories?.name.toLowerCase().includes(searchLower);
      if (!matchesDescription && !matchesCategory) return false;
    }

    return true;
  }).sort((a, b) => new Date(b.transaction_date).getTime() - new Date(a.transaction_date).getTime());

  const clearFilters = () => {
    setFilters({
      type: 'all',
      category: 'all',
      dateFrom: '',
      dateTo: '',
      search: ''
    });
  };

  const hasActiveFilters = filters.type !== 'all' || 
    filters.category !== 'all' || 
    filters.dateFrom !== '' || 
    filters.dateTo !== '' || 
    filters.search !== '';

  const handleDeleteTransaction = async (transactionId: string) => {
    if (!confirm('Are you sure you want to delete this transaction? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingId(transactionId);
      await BudgetingService.deleteBudgetTransaction(transactionId);
      
      // Remove from local state
      setTransactions(prev => prev.filter(t => t.id !== transactionId));
      
      // Trigger refresh event for other components
      window.dispatchEvent(new CustomEvent('budget:refresh'));
      window.dispatchEvent(new CustomEvent('budget:toast', { 
        detail: { type: 'success', message: 'Transaction deleted successfully' } 
      }));
    } catch (error) {
      console.error('Error deleting transaction:', error);
      window.dispatchEvent(new CustomEvent('budget:toast', { 
        detail: { type: 'error', message: 'Failed to delete transaction' } 
      }));
    } finally {
      setDeletingId(null);
    }
  };

  // Calculate totals for filtered transactions
  const totalIncome = filteredTransactions
    .filter(t => t.type === 'income')
    .reduce((sum, t) => sum + Number(t.amount), 0);
  
  const totalExpenses = filteredTransactions
    .filter(t => t.type === 'expense')
    .reduce((sum, t) => sum + Number(t.amount), 0);

  const netAmount = totalIncome - totalExpenses;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white rounded-lg shadow-sm border p-6 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-24 mb-2" />
              <div className="h-7 bg-gray-200 rounded w-32" />
            </div>
          ))}
        </div>
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6 border-b">
            <div className="h-5 bg-gray-200 rounded w-28 animate-pulse" />
          </div>
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="p-6 border-b border-gray-200 animate-pulse">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-gray-200 rounded-lg" />
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-48 mb-2" />
                  <div className="h-3 bg-gray-200 rounded w-32" />
                </div>
                <div className="h-5 bg-gray-200 rounded w-20" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Income</p>
              <p className="text-2xl font-bold text-green-600 mt-1">
                {formatCurrency(totalIncome)}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Expenses</p>
              <p className="text-2xl font-bold text-red-600 mt-1">
                {formatCurrency(totalExpenses)}
              </p>
            </div>
            <div className="p-3 bg-red-100 rounded-full">
              <TrendingDown className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Net Amount</p>
              <p className={`text-2xl font-bold mt-1 ${netAmount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(netAmount)}
              </p>
            </div>
            <div className={`p-3 rounded-full ${netAmount >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
              {netAmount >= 0 ? (
                <TrendingUp className="w-6 h-6 text-green-600" />
              ) : (
                <TrendingDown className="w-6 h-6 text-red-600" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Filter className="w-5 h-5 text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
              {hasActiveFilters && (
                <span className="px-2 py-1 bg-idecide-primary text-white text-xs rounded-full">
                  Active
                </span>
              )}
            </div>
            <div className="flex items-center space-x-2">
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-gray-600 hover:text-gray-900 flex items-center space-x-1"
                >
                  <X className="w-4 h-4" />
                  <span>Clear</span>
                </button>
              )}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {showFilters ? (
                  <ChevronUp className="w-5 h-5 text-gray-600" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-600" />
                )}
              </button>
            </div>
          </div>
        </div>

        {showFilters && (
          <div className="p-4 space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by description or category..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Type Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Transaction Type
                </label>
                <select
                  value={filters.type}
                  onChange={(e) => setFilters({ ...filters, type: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
                >
                  <option value="all">All Types</option>
                  <option value="income">Income</option>
                  <option value="expense">Expense</option>
                  <option value="transfer">Transfer</option>
                </select>
              </div>

              {/* Category Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <select
                  value={filters.category}
                  onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
                >
                  <option value="all">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date Range
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent text-sm"
                    placeholder="From"
                  />
                  <input
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent text-sm"
                    placeholder="To"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Transactions List */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6 border-b flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Transactions</h3>
            <p className="text-sm text-gray-600 mt-1">
              {filteredTransactions.length} {filteredTransactions.length === 1 ? 'transaction' : 'transactions'} found
            </p>
          </div>
          <button className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors">
            <Download className="w-4 h-4" />
            <span className="text-sm">Export</span>
          </button>
        </div>

        <div className="divide-y divide-gray-200">
          {filteredTransactions.length > 0 ? (
            filteredTransactions.map((transaction, index) => (
              <div
                key={transaction.id || index}
                className="p-6 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 flex-1 min-w-0">
                    <div className={`p-3 rounded-lg ${getTransactionBgColor(transaction.type)}`}>
                      {getTransactionIcon(transaction.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {transaction.description || `${transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1)} Transaction`}
                      </p>
                      <div className="flex items-center space-x-3 mt-1">
                        {transaction.budget_categories && (
                          <span
                            className="text-xs px-2 py-1 rounded-full text-white font-medium"
                            style={{ backgroundColor: transaction.budget_categories.color || '#6B7280' }}
                          >
                            {transaction.budget_categories.name}
                          </span>
                        )}
                        <span className="text-xs text-gray-500 flex items-center space-x-1">
                          <Calendar className="w-3 h-3" />
                          <span>{formatDate(transaction.transaction_date)}</span>
                        </span>
                        {transaction.payment_method && (
                          <span className="text-xs text-gray-500 capitalize">
                            • {transaction.payment_method.replace('_', ' ')}
                          </span>
                        )}
                        {transaction.is_recurring && (
                          <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                            Recurring
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="ml-4 flex items-center space-x-4">
                    <div className="text-right">
                      <p className={`text-lg font-semibold ${getTransactionColor(transaction.type)}`}>
                        {transaction.type === 'income' ? '+' : transaction.type === 'expense' ? '-' : ''}
                        {formatCurrency(Number(transaction.amount))}
                      </p>
                    </div>
                    <button
                      onClick={() => transaction.id && handleDeleteTransaction(transaction.id)}
                      disabled={deletingId === transaction.id}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Delete transaction"
                    >
                      {deletingId === transaction.id ? (
                        <div className="w-5 h-5 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Trash2 className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="p-12 text-center">
              <DollarSign className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 text-lg font-medium">No transactions found</p>
              <p className="text-sm text-gray-400 mt-2">
                {hasActiveFilters 
                  ? 'Try adjusting your filters to see more results'
                  : 'Add your first transaction to get started'
                }
              </p>
              {!hasActiveFilters && (
                <button
                  onClick={() => window.dispatchEvent(new CustomEvent('budget:open-modal', { detail: 'income' }))}
                  className="mt-4 bg-idecide-primary text-white px-4 py-2 rounded-lg hover:bg-idecide-primary/90 transition-colors text-sm"
                >
                  Add Transaction
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

